import React from "react";
import { Link, useLocation } from "react-router-dom";
import { XCircle, RefreshCw, ExternalLink } from "lucide-react";

const PaymentFailedPage = () => {
  const location = useLocation();
  const { message = "", mode = "paid" } = location.state || {};

  // Detect common Firebase auth errors and provide guidance
  const isFirebaseNetworkErr = /network-request-failed/i.test(message);
  const isFirebaseOpNotAllowed = /operation-not-allowed/i.test(message);
  const isEmailInUse = /email-already-in-use/i.test(message);
  const isWeakPassword = /weak-password/i.test(message);

  const isFreeFlow = mode === "free";
  const title = isFreeFlow ? "Registration Failed" : "Payment Failed";

  let helpfulMsg = isFreeFlow
    ? "Your coupon was valid, but your account couldn't be created. No data has been saved."
    : "Your registration has not been completed because the payment was unsuccessful. No data has been saved.";

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-xl bg-white border border-slate-200 rounded-2xl shadow-sm p-10 text-center" data-testid="payment-failed-card">
        <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-10 h-10 text-red-600" />
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">{title}</h1>

        {message && (
          <p className="mt-3 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-4 py-2 font-mono inline-block">
            {message}
          </p>
        )}

        <p className="mt-4 text-slate-600">{helpfulMsg}</p>

        {/* Contextual guidance */}
        {isFirebaseNetworkErr && (
          <div className="mt-6 text-left bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm">
            <div className="font-semibold text-amber-900 mb-2">🔧 Firebase setup required</div>
            <p className="text-amber-800 mb-3">
              This error means Firebase is blocking the request. Most likely:
            </p>
            <ol className="list-decimal pl-5 space-y-2 text-amber-900">
              <li>
                Add this preview domain to <strong>Firebase → Authentication → Settings → Authorized domains</strong>:{" "}
                <code className="bg-white px-2 py-0.5 rounded text-xs">{window.location.hostname}</code>
              </li>
              <li>
                Ensure <strong>Email/Password sign-in</strong> is enabled in <strong>Authentication → Sign-in method</strong>.
              </li>
              <li>
                Ensure a <strong>Firestore database</strong> is created in your Firebase project.
              </li>
            </ol>
            <a
              href="https://console.firebase.google.com/project/kampus-companion/authentication/settings"
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex items-center gap-1 text-amber-900 font-semibold hover:underline"
            >
              Open Firebase Auth Settings <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        )}

        {isFirebaseOpNotAllowed && (
          <div className="mt-6 text-left bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm">
            <div className="font-semibold text-amber-900 mb-2">🔧 Email/Password sign-in is disabled</div>
            <p className="text-amber-800">
              Open Firebase Console → Authentication → Sign-in method → enable <strong>Email/Password</strong>.
            </p>
            <a
              href="https://console.firebase.google.com/project/kampus-companion/authentication/providers"
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-1 text-amber-900 font-semibold hover:underline"
            >
              Open Sign-in Providers <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        )}

        {isEmailInUse && (
          <div className="mt-6 text-left bg-blue-50 border border-blue-200 rounded-xl p-5 text-sm">
            <div className="font-semibold text-blue-900 mb-2">This email is already registered</div>
            <p className="text-blue-800">
              Try logging in instead, or use a different email address for registration.
            </p>
            <Link to="/login" className="mt-3 inline-flex items-center gap-1 text-blue-900 font-semibold hover:underline">
              Go to Login →
            </Link>
          </div>
        )}

        {isWeakPassword && (
          <div className="mt-6 text-left bg-blue-50 border border-blue-200 rounded-xl p-5 text-sm">
            <div className="font-semibold text-blue-900 mb-2">Password too weak</div>
            <p className="text-blue-800">Please use a password with at least 6 characters.</p>
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Link to="/register" data-testid="failed-retry-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-all">
            <RefreshCw className="w-4 h-4" /> Retry Registration
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold transition-all">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PaymentFailedPage;
